<template>
  <v-container class="fill-height pa-0">
    <v-overlay v-if="loading">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </v-overlay>
    <l-map
      id="map"
      ref="map"
      :zoom="10"
      :center="initialLocation"
      :v-resize="onResize"
      :options="{ zoomControl: false, attributionControl: false }"
    >
      <l-control-zoom position="bottomright" />
      <l-control-layers position="bottomleft" />
      <l-tile-layer
        v-for="provider in tileProviders"
        :key="provider.id"
        :name="provider.name"
        :url="provider.url"
        layer-type="base"
      />
      <l-marker-cluster ref="immoCluster" :options="clusterOptionsImmo">
        <!-- this.$refs.immoCluster.mapObject.refreshClusters() -->
        <l-marker
          v-for="immo in immoMap"
          :key="immo.id"
          :lat-lng="[immo.location.lat, immo.location.lng]"
          :icon="immoIcon(immo.meta)"
        >
          <l-popup>
            <v-card width="300">
              <v-img
                v-if="immo.info.thumb"
                :src="imageThumbURL(immo.info.thumb)"
                :aspect-ratio="1.77777778"
                @error="imageLoadError"
                ><template v-slot:placeholder>
                  <v-row
                    class="fill-height ma-0"
                    align="center"
                    justify="center"
                  >
                    <v-progress-circular
                      indeterminate
                      color="grey lighten-2"
                    ></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
              <v-card-subtitle class="pb-0">
                {{ immo.info.title }}
              </v-card-subtitle>

              <v-card-text class="pb-0 mb-0">
                <v-row justify="space-around" cols="12">
                  <v-col cols="7">
                    <v-chip v-if="immo.properties.living" small outlined label>
                      <v-icon left>flip_to_front</v-icon>
                      {{ immo.properties.living }} m<sup>2</sup>
                    </v-chip>
                    <v-chip
                      v-if="immo.properties.property"
                      small
                      outlined
                      label
                    >
                      <v-icon left>flip_to_back</v-icon>
                      {{ immo.properties.property }} m<sup>2</sup>
                    </v-chip>
                  </v-col>
                  <v-col cols="5">
                    <v-chip
                      v-if="immo.properties.bedrooms"
                      small
                      outlined
                      label
                    >
                      <v-icon left>single_bed</v-icon>
                      {{ immo.properties.bedrooms }}
                    </v-chip>
                    <v-chip
                      v-if="immo.properties.bathrooms"
                      small
                      outlined
                      label
                    >
                      <v-icon left>bathtub</v-icon>
                      {{ immo.properties.bathrooms }}
                    </v-chip>
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-text class="pt-0 mt-0">
                <v-row justify="space-around">
                  <!-- Sea Access -->
                  <v-chip
                    v-if="immo.properties.sea_access"
                    small
                    outlined
                    label
                  >
                    <v-icon left>rowing</v-icon>
                  </v-chip>
                  <v-chip v-if="immo.properties.sea_view" small outlined label>
                    <v-icon small>shopping_basket</v-icon>
                    <!-- Sea View -->
                  </v-chip>
                  <v-chip v-if="immo.properties.pool" small outlined label>
                    <v-icon small>view_day</v-icon>
                    <!-- Pool -->
                  </v-chip>
                  <v-chip v-if="immo.properties.golf" small outlined label>
                    <v-icon small>markunread_mailbox</v-icon>
                    <!-- Golf -->
                  </v-chip>
                  <v-chip v-if="immo.properties.garage" small outlined label>
                    <v-icon small>store</v-icon>
                    <!-- Garage -->
                  </v-chip>
                  <v-chip v-if="immo.properties.parking" small outlined label>
                    <v-icon small>local_parking</v-icon>
                    <!-- Parking -->
                  </v-chip>
                  <v-chip v-if="immo.properties.rental" small outlined label>
                    <v-icon small>money</v-icon>
                    <!-- Rental Licence -->
                  </v-chip>
                  <v-chip
                    v-if="immo.properties.investment"
                    small
                    outlined
                    label
                  >
                    <v-icon small>euro</v-icon>
                    <!-- Investment -->
                  </v-chip>
                </v-row>
              </v-card-text>

              <v-card-text class="text-center pt-0 pb-0">
                {{ immo.properties.price | formatPrice }}
              </v-card-text>

              <v-card-actions class="pt-0 pb-0">
                <router-link :to="'/immo/' + immo.id" target="_blank">
                  <v-btn small icon>
                    <v-icon>edit</v-icon>
                  </v-btn>
                </router-link>
                <v-spacer></v-spacer>
                <span class="grey--text overline">#{{ immo.meta.ref }}</span>
                <v-spacer></v-spacer>
                <v-btn small icon @click="viewImmo = immo">
                  <v-icon>aspect_ratio</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </l-popup>
        </l-marker>
      </l-marker-cluster>

      <!-- Customers -->
      <l-marker-cluster ref="customerCluster" :options="clusterOptionsCustomer">
        <!-- this.$refs.customerCluster.mapObject.refreshClusters() -->
        <l-marker
          v-for="c in customerList"
          :key="c.id"
          :lat-lng="[c.location.lat, c.location.lng]"
          :icon="customerIcon()"
        >
          <l-popup>
            <v-card width="250">
              <v-card-title class="title font-weight-light">
                {{ c.first_name }} {{ c.last_name }}
              </v-card-title>

              <v-card-text class="pa-2">
                {{ c.categories.join(' | ') }}
              </v-card-text>

              <v-card-text>
                {{ c.info }}
              </v-card-text>

              <v-card-text>
                <v-row justify="space-around">
                  <v-chip v-if="c.properties.living" small outlined label>
                    <v-icon left>flip_to_front</v-icon>
                    {{ c.properties.living }} m<sup>2</sup>
                  </v-chip>
                  <v-chip v-if="c.properties.property" small outlined label>
                    <v-icon left>flip_to_back</v-icon>
                    {{ c.properties.property }} m<sup>2</sup>
                  </v-chip>
                  <v-chip v-if="c.properties.bedrooms" small outlined label>
                    <v-icon left>single_bed</v-icon>
                    {{ c.properties.bedrooms }}
                  </v-chip>
                  <v-chip v-if="c.properties.bathrooms" small outlined label>
                    <v-icon left>bathtub</v-icon>
                    {{ c.properties.bathrooms }}
                  </v-chip>
                </v-row>
              </v-card-text>
              <v-card-text>
                <v-row justify="space-around">
                  <v-chip v-if="c.properties.sea_access" small outlined label>
                    <v-icon left>rowing</v-icon>
                    Sea Access
                  </v-chip>
                  <v-chip v-if="c.properties.sea_view" small outlined label>
                    <v-icon left>shopping_basket</v-icon>
                    Sea View
                  </v-chip>
                  <v-chip v-if="c.properties.pool" small outlined label>
                    <v-icon left>view_day</v-icon>
                    Pool
                  </v-chip>
                  <v-chip v-if="c.properties.golf" small outlined label>
                    <v-icon small left>markunread_mailbox</v-icon>
                    Golf
                  </v-chip>
                  <v-chip v-if="c.properties.garage" small outlined label>
                    <v-icon left>store</v-icon>
                    Garage
                  </v-chip>
                  <v-chip v-if="c.properties.parking" small outlined label>
                    <v-icon left>settings_cell</v-icon>
                    Parking
                  </v-chip>
                  <v-chip v-if="c.properties.rental" small outlined label>
                    <v-icon left>money</v-icon>
                    Rental Licence
                  </v-chip>
                  <v-chip v-if="c.properties.investment" small outlined label>
                    <v-icon left>euro</v-icon>
                    Investment
                  </v-chip>
                </v-row>
              </v-card-text>

              <v-expansion-panels flat tile hover>
                <v-expansion-panel>
                  <v-expansion-panel-header
                    class="subtitle-2 text-end pt-0 pb-0"
                    >{{
                      c.properties.price | formatPrice
                    }}</v-expansion-panel-header
                  >
                  <v-expansion-panel-content>
                    <div>Email: {{ c.email }}</div>
                    <div>Phone: {{ c.phone }}</div>
                    <div>Comment: {{ c.comment }}</div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>

              <v-card-actions>
                <v-spacer></v-spacer>
                <span class="grey--text">Ref {{ c.ref }}</span>
                <v-spacer></v-spacer>
                <button v-on:click="filterForCustomer(c)">
                  <v-icon>search</v-icon>
                </button>
              </v-card-actions>
            </v-card>
          </l-popup>
        </l-marker>
      </l-marker-cluster>
    </l-map>

    <v-fab-transition>
      <v-btn
        @click.stop="drawerFilter = !drawerFilter"
        fab
        small
        absolute
        top
        right
        class="mt-8"
      >
        <v-icon>filter_list</v-icon>
      </v-btn>
    </v-fab-transition>

    <v-navigation-drawer v-model="drawerFilter" app right stateless>
      <ImmoFilter :filter="filter" @filterUpdated="filterUpdated" />
    </v-navigation-drawer>
    <ImmoDialog
      v-if="viewImmo"
      :immo="viewImmo"
      @closed="viewImmo = undefined"
    />
  </v-container>
</template>

<script>
import ImmoFilter from '@/components/immo/Filter'
import ImmoDialog from '@/components/immo/Dialog'
import { IMMO_MAP_REQUEST } from '@/store/actions'
import { mapGetters, mapActions } from 'vuex'
import Markers from '@/assets/js/leaflet-color-markers'

import {
  LMap,
  LTileLayer,
  LControlZoom,
  LControlLayers,
  LMarker,
  LPopup
} from 'vue2-leaflet'
import Vue2LeafletMarkerCluster from 'vue2-leaflet-markercluster'

export default {
  name: 'Map',
  components: {
    ImmoFilter,
    ImmoDialog,
    LMap,
    LTileLayer,
    LControlZoom,
    LControlLayers,
    LMarker,
    LPopup,
    'l-marker-cluster': Vue2LeafletMarkerCluster
  },
  data() {
    return {
      viewImmo: null,
      drawerFilter: true,
      loading: false,
      filter: {
        showCustomers: false,
        page: 1,
        limit: '1000',
        hideLimit: true,
        categories: ['VILLA', 'FINCA', 'PENTHOUSE'],
        payment: 'BUY',
        status: ['AUTHORISED']
      },
      initialLocation: [39.6141, 2.9108],
      tileProviders: [
        {
          id: 1,
          name: 'Open Street Map',
          url: 'https://{s}.tile.osm.org/{z}/{x}/{y}.png',
          attribution: 'OSM'
        },
        {
          id: 2,
          name: 'cartocdn',
          url:
            'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
          attribution:
            "&copy; <a href='http://www.openstreetmap.org/copyright'>OpenStreetMap</a> &copy; <a href='https://carto.com/attributions'>CARTO</a>"
        },
        {
          id: 3,
          name: 'Esri World Map',
          url:
            'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
          attribution: 'Esri'
        },
        {
          id: 4,
          name: 'Esri World Imagery',
          url:
            'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
          attribution: 'Esri'
        }
      ],
      clusterOptionsImmo: {
        spiderfyOnMaxZoom: false,
        disableClusteringAtZoom: 16
      },
      clusterOptionsCustomer: {
        spiderfyOnMaxZoom: false,
        disableClusteringAtZoom: 16
      }
    }
  },
  computed: {
    ...mapGetters(['immoMap', 'mapCount', 'customerList'])
  },
  methods: {
    ...mapActions([IMMO_MAP_REQUEST]),

    async filterUpdated(filter) {
      this.drawerFilter = false
      this.loading = true
      await this.IMMO_MAP_REQUEST(filter)
      this.loading = false
    },

    async filterForCustomer(c) {
      //console.log(c.properties)
      let f = {
        showCustomers: true,
        status: ['INPROGRESS', 'AUTHORISED', 'CONTACT', 'UNKNOWN', 'SOLD'],
        categories: c.categories,
        payment: c.payment
      }

      if (c.properties.price) {
        f.price__gte = c.properties.price * 0.75
        f.price__lte = c.properties.price * 1.25
      }
      if (c.properties.bedrooms) f.bedrooms__gte = c.properties.bedrooms
      if (c.properties.bathrooms) f.bathrooms__gte = c.properties.bathrooms
      if (c.properties.living) f.living__gte = c.properties.living
      if (c.properties.terrace) f.terrace__gte = c.properties.terrace
      if (c.properties.property) f.property__gte = c.properties.property

      if (c.properties.garage) f.garage = true
      if (c.properties.parking) f.parking = true
      if (c.properties.golf) f.golf = true
      if (c.properties.investment) f.investment = true
      if (c.properties.pool) f.pool = true
      if (c.properties.rental) f.rental = true
      if (c.properties.sea_view) f.sea_view = true
      if (c.properties.sea_access) f.sea_access = true
      //console.log(f)

      this.loading = true
      await this.IMMO_MAP_REQUEST(f)
      this.loading = false
    },

    onResize() {
      this.$refs.map.mapObject.invalidateSize()
    },
    imageURL(filename) {
      return `/images/${filename}`
    },
    imageThumbURL(filename) {
      const thumb = filename.replace(/scaled/, 'thumbnail')
      return `/images/${thumb}`
    },
    imageLoadError(img) {
      console.log('failed to load image')
      console.log(img)
    },
    immoIcon(meta) {
      switch (meta.categories[0]) {
        case 'VILLA':
          return Markers.blue
        case 'APARTMENT':
          return Markers.orange
        case 'FINCA':
          return Markers.green
        case 'PENTHOUSE':
          return Markers.purple
        case 'COMMUNITY':
          return Markers.yellow
        case 'BUSINESS':
          return Markers.grey
        case 'PLOT':
          return Markers.red
        default:
          return Markers.blue
      }
    },
    customerIcon() {
      return Markers.teal
    }
  }
}
</script>

<style lang="scss">
@import '~leaflet.markercluster/dist/MarkerCluster.css';
@import '~leaflet.markercluster/dist/MarkerCluster.Default.css';

#map {
  z-index: 0;
}

.leaflet-popup {
  position: absolute;
  margin-bottom: 20px;
  padding: 0px;
}

.leaflet-popup-content-wrapper {
  padding: 0px;
  text-align: inherit;
  border-radius: 0px;
  background: transparent;
}

.leaflet-popup-close-button {
  position: absolute;
  top: 0;
  left: 0;
  padding: 4px 4px 0 0;
  border: none;
  text-align: center;
  width: 18px;
  height: 14px;
  font: 16px/14px Tahoma, Verdana, sans-serif;
  color: #c3c3c3;
  text-decoration: none;
  font-weight: bold;
  background: transparent;
}

.v-chip {
  margin: 2px;
}
</style>
